import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Building,
  FileText,
  Hash,
  Mail,
  Pencil,
  MapPin,
  CreditCard,
  Handshake,
} from "lucide-react";
import { isDanishVenue } from "@/lib/utils/company-info";

interface CompanyInfoCardProps {
  booking: any;
  customer: any;
  onEdit?: () => void;
  canEdit?: boolean;
  /** With the venue's default language, decides "CVR"/"EAN" (Danish venue). */
  venueCurrency?: string | null;
  venueDefaultLanguage?: string | null;
}

export function CompanyInfoCard({
  booking,
  customer,
  onEdit,
  canEdit = true,
  venueCurrency,
  venueDefaultLanguage,
}: CompanyInfoCardProps) {
  // Check if customer has a company profile
  const companyProfile = customer?.company_profile;
  // At a Danish venue the Peppol ID is the EAN number, shown under the CVR.
  const showEan = isDanishVenue(venueCurrency, venueDefaultLanguage);

  // This component is only rendered for corporate bookings,
  // so we always show it regardless of data availability

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            Company & Invoice Information
          </CardTitle>
          {canEdit && onEdit && (
            <Button variant="ghost" size="sm" onClick={onEdit}>
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Partner agency that brokered the booking */}
        {booking.partner?.company_name && (
          <div className="flex items-center gap-3">
            <Handshake className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-gray-500">Partner</p>
              <p className="font-medium">
                {booking.partner.company_name}
                {booking.partner_commission_percentage != null && (
                  <span className="font-normal text-gray-500">
                    {" "}
                    ({Number(booking.partner_commission_percentage)}% commission)
                  </span>
                )}
              </p>
              {booking.end_customer_name && (
                <p className="text-sm text-gray-500">
                  On behalf of {booking.end_customer_name}
                </p>
              )}
            </div>
          </div>
        )}
        {/* Only show fields if there's actual data */}
        {(companyProfile?.company_name || customer?.company_name || booking.company_name) && (
          <div className="flex items-center gap-3">
            <Building className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Company Name</p>
              <p className="font-medium">
                {companyProfile?.company_name ||
                  customer?.company_name ||
                  booking.company_name}
              </p>
              {companyProfile?.organization_number && (
                <p className="text-sm text-gray-500 mt-1">
                  {companyProfile.organization_number}
                </p>
              )}
              {showEan && companyProfile?.peppol_id && (
                <p className="text-sm text-gray-500">
                  EAN {companyProfile.peppol_id}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Company Address - only show if data exists */}
        {companyProfile?.address_line_1 && (
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Company Address</p>
              <div className="font-medium">
                <p>{companyProfile.address_line_1}</p>
                {companyProfile.address_line_2 && (
                  <p>{companyProfile.address_line_2}</p>
                )}
                {(companyProfile.city || companyProfile.postcode) && (
                  <p>
                    {companyProfile.postcode && `${companyProfile.postcode} `}
                    {companyProfile.city}
                  </p>
                )}
                {companyProfile.country && <p>{companyProfile.country}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Cost Center */}
        {booking.cost_center && (
          <div className="flex items-center gap-3">
            <Hash className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Cost Center</p>
              <p className="font-medium">{booking.cost_center}</p>
            </div>
          </div>
        )}

        {/* Corporate Contact */}
        {booking.corporate_contact && (
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Corporate Contact</p>
              <p className="font-medium">{booking.corporate_contact}</p>
            </div>
          </div>
        )}

        {/* Invoice Type */}
        {companyProfile?.invoice_type && (
          <div className="flex items-center gap-3">
            <CreditCard className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Invoice Type</p>
              <p className="font-medium">
                {companyProfile.invoice_type === "e_invoice"
                  ? "E-Invoice"
                  : "PDF Invoice"}
              </p>
            </div>
          </div>
        )}

        {/* Peppol ID */}
        {!showEan &&
          companyProfile?.invoice_type === "e_invoice" &&
          companyProfile?.peppol_id && (
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-gray-400" />
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Peppol ID</p>
                <p className="font-medium">{companyProfile.peppol_id}</p>
              </div>
            </div>
          )}

        {/* Invoice Email */}
        {companyProfile?.invoice_email && (
          <div className="flex items-center gap-3">
            <Mail className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Invoice Email</p>
              <p className="font-medium">{companyProfile.invoice_email}</p>
            </div>
          </div>
        )}

        {/* Invoice Reference */}
        {(companyProfile?.invoice_reference || booking.invoice_reference) && (
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Invoice Reference</p>
              <p className="font-medium">
                {companyProfile?.invoice_reference || booking.invoice_reference}
              </p>
            </div>
          </div>
        )}

        {/* Show message if no company data available */}
        {!companyProfile?.company_name &&
          !customer?.company_name &&
          !booking.company_name &&
          !companyProfile?.address_line_1 &&
          !booking.invoice_reference &&
          !booking.cost_center &&
          !booking.corporate_contact &&
          !companyProfile?.invoice_email &&
          !companyProfile?.invoice_type && (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">
                No company information available
              </p>
            </div>
          )}
      </CardContent>
    </Card>
  );
}
